import { getActiveOrderDrugs } from '../overview/orderDrugLinking';

/** Mirrors the backend TERMINAL_ORDER_STAGES / TERMINAL_ORDER_STATUSES sets. */
const TERMINAL_STAGES = new Set(['delivered', 'picked_up', 'cancelled', 'delivery_missed']);
const TERMINAL_STATUSES = new Set(['completed', 'cancelled', 'failed']);

const isCancelled = (ord: any): boolean =>
  String(ord?.status ?? '').toLowerCase() === 'cancelled' ||
  String(ord?.workflowStage ?? '').toLowerCase() === 'cancelled';

/** Still somewhere in fulfilment — the urgent half of a duplicate pair. */
export const isOpenOrder = (ord: any): boolean =>
  !TERMINAL_STAGES.has(String(ord?.workflowStage ?? '').toLowerCase()) &&
  !TERMINAL_STATUSES.has(String(ord?.status ?? '').toLowerCase());

/**
 * The order number a derived order descends from. PHOX spawns children that
 * legitimately re-use the parent's Rx and are ALREADY linked elsewhere in the
 * UI, so they must never read as accidental duplicates:
 *   - `BO-ORD-…`      back-order / balance-owing remainder (packaging split)
 *   - `CC-ORD-…`      cold-chain half (GP-75)
 *   - `TS-ORD-…`      troubleshooting split (badged "Split · N items")
 *   - `RP-ORD-…`      reprocess replacement (GP-1030) — shares the source's Rx by design
 *   - `BO-TS-ORD-…-1776194605769`  combinations, plus a ms-epoch re-split suffix
 * Only the ≥10-digit epoch suffix is stripped — the 3-digit tail of a normal
 * `ORD-20260813-141516-800` is part of its identity and must stay.
 */
export const getOrderFamilyKey = (orderNumber?: string): string => {
  const raw = String(orderNumber || '');
  const prefixes = raw.match(/^((?:(?:BO|CC|TS|RP)-)+)/)?.[1] ?? '';
  let key = raw.slice(prefixes.length).replace(/-\d{10,}$/, '');
  if (/(?:^|-)RP-/.test(prefixes)) {
    // A re-reprocess appends a small retry counter AFTER the source's 3-digit
    // tail (`RP-ORD-…-497-2`, see generateReprocessOrderNumber). Strip it only
    // when the remainder still ends in the full 3-digit tail, so the identity
    // tail of a first replacement is never eaten.
    const retry = key.match(/^(.+-\d{3})-\d{1,2}$/);
    if (retry) key = retry[1];
  }
  return key;
};

export interface DuplicateRxSibling {
  id: string;
  orderNumber: string;
  workflowStage?: string;
  status?: string;
}

export interface DuplicateRxInfo {
  /** Rx numbers this order shares with an unrelated order. */
  rxNumbers: string[];
  /** The unrelated orders carrying them. */
  siblings: DuplicateRxSibling[];
  /** True when this order or any sibling is still in fulfilment (double-dispense risk). */
  anyOpen: boolean;
}

/**
 * GP-1391 — find orders in a patient's history that carry the SAME prescription
 * number as an unrelated order.
 *
 * Reported case: one patient had ORD-20260813-141516-800 (Product
 * Verification) and ORD-20260813-135932-741 (Ready to Ship) both showing
 * Rx 1483723, same drug, same Aug 14 pickup, $65.01 pending on each. The two
 * Order History rows were visually identical apart from the order number and
 * stage badge, so the reporter had to open both detail views and compare the
 * prescription hardcopies by eye to confirm the collision.
 *
 * Rules:
 *   - Cancelled orders are ignored — a cancel-then-recreate legitimately reuses
 *     the Rx.
 *   - Orders of the same FAMILY are ignored (see `getOrderFamilyKey`): BO- / CC-
 *     / TS- children share their parent's Rx by design. Measured on prod, this
 *     leaves exactly the real collisions and no false positives.
 *   - Rx numbers come from `getActiveOrderDrugs`, the same source as the table's
 *     Rx column, so split-off / removed / zero-fill drugs don't raise a flag.
 *
 * Pure: returns a map keyed by order id; renders nothing, mutates nothing.
 */
export function findDuplicateRxOrders(orders: any[]): Map<string, DuplicateRxInfo> {
  const result = new Map<string, DuplicateRxInfo>();
  if (!Array.isArray(orders) || orders.length < 2) return result;

  // The same order can appear twice in a merged list — dedupe by id.
  const considered = Array.from(
    new Map(
      orders.filter((ord) => ord?.id && ord?.orderNumber && !isCancelled(ord)).map((ord) => [ord.id, ord]),
    ).values(),
  );

  const byRx = new Map<string, any[]>();
  for (const ord of considered) {
    for (const rx of getActiveOrderDrugs(ord).rxNumbers) {
      const list = byRx.get(rx) || [];
      list.push(ord);
      byRx.set(rx, list);
    }
  }

  for (const [rx, group] of byRx.entries()) {
    if (group.length < 2) continue;
    for (const ord of group) {
      const family = getOrderFamilyKey(ord.orderNumber);
      const siblings = group.filter(
        (other) => other.id !== ord.id && getOrderFamilyKey(other.orderNumber) !== family,
      );
      if (siblings.length === 0) continue;

      const info: DuplicateRxInfo = result.get(ord.id) || {
        rxNumbers: [],
        siblings: [],
        anyOpen: false,
      };
      if (!info.rxNumbers.includes(rx)) info.rxNumbers.push(rx);
      for (const sibling of siblings) {
        if (info.siblings.some((known) => known.id === sibling.id)) continue;
        info.siblings.push({
          id: sibling.id,
          orderNumber: sibling.orderNumber,
          workflowStage: sibling.workflowStage,
          status: sibling.status,
        });
      }
      info.anyOpen = info.anyOpen || isOpenOrder(ord) || siblings.some(isOpenOrder);
      result.set(ord.id, info);
    }
  }

  return result;
}
